import { dropTask } from 'ember-concurrency';
import { modifier } from 'ember-modifier';
import {
  phoneCapturePromptShownRequest,
  type PhoneCapturePromptSnoozeKind,
} from 'tio-data/requests/phone-capture-prompt';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import Drawer from 'tio-ui/components/drawer';
import matchMedia from 'tio-common/helpers/match-media';
import Modal from 'tio-ui/components/modal';
import PhoneVerificationFlow from '../phone-verification/flow';
import { findRecord as findRecordRequest } from '@warp-drive/utilities/json-api';
import type { Store as StoreService } from '@warp-drive/core';
import type SessionContextService from 'tio-employee/services/session-context';
import type PhoneCaptureSnoozeService from 'tio-employee/services/phone-capture-snooze';
import type { TOC } from '@ember/component/template-only';

/** Above this the prompt is a centred modal; below it, a bottom sheet. */
export const DESKTOP_QUERY = '(min-width: 640px)';

/**
 * Reports the render exactly once. The cadence advances on the prompt actually
 * appearing, not on eligibility being read, so this cannot live in a getter.
 */
const reportShown = modifier((_element: HTMLElement, [notify]: [() => void]) => {
  notify();
});

interface PromptBodySignature {
  Args: {
    /** `desktop` or `mobile`: names the chrome, and the sheet is too narrow for the
     * field and Send side by side. */
    variant: string;
    /** Verified or deferred — either way prompting is over. */
    onFinish: () => void;
    onShown: () => void;
  };
}

interface PromptFooterSignature {
  Args: {
    onRemindLater: () => void;
    isSnoozing: boolean;
  };
}

/** One body for both chromes, so the two cannot drift apart. */
const PromptBody: TOC<PromptBodySignature> = <template>
  <div class="tio-col-loose" data-test-phone-capture-prompt={{@variant}} {{reportShown @onShown}}>
    <PhoneVerificationFlow
      @surface="postLoginOverlay"
      @intro={{t "phone_verification.prompt.benefit"}}
      @entryLayout={{if (eq @variant "desktop") "inline" "stacked"}}
      @onVerified={{@onFinish}}
      @onDeferred={{@onFinish}}
    />
  </div>
</template>;

/** The deliberate exit, in the chrome's footer so it reads as one. */
const PromptFooter: TOC<PromptFooterSignature> = <template>
  <div class="tio-col-flush items-center">
    <Button
      @appearance="minimal"
      @size="sm"
      @isRunning={{@isSnoozing}}
      {{on "click" @onRemindLater}}
      data-test-phone-capture-prompt-remind-later
    >{{t "phone_verification.prompt.remind_later"}}</Button>
    <p class="tio-text-xs-normal tio-fg-muted">{{t "phone_verification.prompt.profile_note"}}</p>
  </div>
</template>;

export interface PhoneCapturePromptSignature {
  Args: {
    /**
     * Called once the prompt has finished with itself — dismissed, verified or
     * deferred. The caller hides it without a page reload; the cadence is already
     * persisted by then.
     */
    onDone: () => void;
  };
}

/**
 * The post-login mobile-capture prompt: a desktop modal or a mobile bottom sheet
 * around the shared verification flow.
 *
 * Every render gate lives in the caller (eligibility, capability, terms, demo
 * sessions) so this is only mounted when it should already be visible.
 */
export default class PhoneCapturePrompt extends Component<PhoneCapturePromptSignature> {
  @service declare store: StoreService;
  @service declare sessionContext: SessionContextService;
  @service declare phoneCaptureSnooze: PhoneCaptureSnoozeService;

  desktopQuery = DESKTOP_QUERY;

  /** A failed report must not hide the prompt — the member still sees a usable one. */
  announce = dropTask(async () => {
    try {
      await this.store.request(phoneCapturePromptShownRequest(this.platform));
    } catch {
      // The cadence tolerates a missed render; an unusable prompt does not.
    }
  });

  /**
   * Awaited before teardown, so a dismissal followed immediately by a reload still
   * finds the snooze recorded. A rejected snooze still closes — leaving the member
   * trapped in a prompt they dismissed is the worse failure.
   */
  snooze = dropTask(async (kind: PhoneCapturePromptSnoozeKind) => {
    try {
      await this.phoneCaptureSnooze.snooze(kind, this.platform);
    } catch {
      // A lost snooze only means both prompts are offered again sooner.
    } finally {
      this.args.onDone();
    }
  });

  get platform(): string {
    return window.matchMedia(DESKTOP_QUERY).matches ? 'desktop' : 'mobile';
  }

  /**
   * Crossing the 640px breakpoint tears down one chrome and inserts the other, which
   * re-runs the insert modifier. The cadence counts prompts, not chromes.
   */
  hasReportedShown = false;

  notifyShown = () => {
    if (this.hasReportedShown) return;

    this.hasReportedShown = true;
    this.announce.perform();
  };

  /** `×` means "not now": both prompts return at the next login. */
  dismissSoftly = () => {
    this.snooze.perform('soft');
  };

  /** "Remind me later" is the deliberate, longer suppression. */
  remindLater = () => {
    this.snooze.perform('long');
  };

  /** The dashboard underneath reads the employee's phone rows, so re-read them or its
   * attention row keeps asking. Not `sessionContext.load`: it logs out on any failure. */
  finish = dropTask(async () => {
    try {
      const id = this.sessionContext.currentEmployee?.id;
      if (id) {
        await this.store.request(
          findRecordRequest('employee', id, { include: 'phone-numbers', reload: true })
        );
      }
    } catch {
      // A stale row until the next load beats a prompt that will not close.
    } finally {
      this.args.onDone();
    }
  });

  <template>
    {{#if (matchMedia this.desktopQuery)}}
      <Modal
        @isOpen={{true}}
        @onClose={{this.dismissSoftly}}
        @size="lg"
        @ariaLabel={{t "phone_verification.number.title"}}
        @closeLabel={{t "common.close"}}
      >
        <:body>
          <PromptBody
            @variant="desktop"
            @onFinish={{this.finish.perform}}
            @onShown={{this.notifyShown}}
          />
        </:body>
        <:footer>
          <PromptFooter @onRemindLater={{this.remindLater}} @isSnoozing={{this.snooze.isRunning}} />
        </:footer>
      </Modal>
    {{else}}
      {{! manageHistory defaults to true, which lets history.back() dismiss the sheet —
          the wrong shape for a prompt whose dismissal has to be recorded. }}
      <Drawer
        @isOpen={{true}}
        @onClose={{this.dismissSoftly}}
        @placement="bottom"
        @size="xl"
        @manageHistory={{false}}
        @ariaLabel={{t "phone_verification.number.title"}}
        @closeLabel={{t "common.close"}}
      >
        <:body>
          <PromptBody
            @variant="mobile"
            @onFinish={{this.finish.perform}}
            @onShown={{this.notifyShown}}
          />
        </:body>
        <:footer>
          <PromptFooter @onRemindLater={{this.remindLater}} @isSnoozing={{this.snooze.isRunning}} />
        </:footer>
      </Drawer>
    {{/if}}
  </template>
}
